import type {
  CoachingQuotaLedger,
  CoachingSessionType,
} from '@efdigital/ll-addon-data';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import {
  formatQuotaLedgerReason,
  formatSessionType,
} from '@efdigital/ll-admin-v2/utils/coaching/coaching-sessions/quota-ledger-display';
import { netQuotaForSession } from '@efdigital/ll-admin-v2/utils/coaching/coaching-sessions/resolve-outcomes';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { ResultsTable } from '../../results-table.gts';

interface CoachingQuotaLedgersSignature {
  Args: {
    quotaLedgers: CoachingQuotaLedger[];
    sessionType: CoachingSessionType;
  };
  Element: HTMLDivElement;
}

function formatQuotaLedgerAmount(
  amount: number,
  sessionType: CoachingSessionType
) {
  const prefix = amount <= 0 ? `${amount}` : `+${amount}`;
  if (sessionType === 'coaching') {
    return `${prefix} min`;
  }
  return `${prefix} session${Math.abs(amount) === 1 ? '' : 's'}`;
}

function colorClassForAmount(amount: number) {
  if (amount === 0) {
    return '';
  } else if (amount < 0) {
    return 'text-danger';
  }
  return 'text-success';
}

export class CoachingQuotaLedgers extends Component<CoachingQuotaLedgersSignature> {
  @service declare store: Store;
  @service declare userLookup: UserLookupService;

  lookupUsers = async () => {
    const userIds = this.args.quotaLedgers.map((ledger) => ledger.user);
    await this.userLookup.lookupUsers(userIds);
  };

  <template>
    <div ...attributes>
      <Await @promise={{(this.lookupUsers)}}>
        <:success>
          <ResultsTable @data={{@quotaLedgers}}>
            <:header>
              <th>Amount</th>
              <th>Type</th>
              <th>Reason</th>
              <th>User</th>
            </:header>
            <:row as |ledger|>
              <tr>
                <td
                  class="font-monospace {{colorClassForAmount ledger.amount}}"
                >{{formatQuotaLedgerAmount
                    ledger.amount
                    ledger.sessionType
                  }}</td>
                <td>{{formatSessionType ledger.sessionType}}</td>
                <td>{{formatQuotaLedgerReason ledger.reason}}</td>
                <td>{{#let (this.userLookup.fromCache ledger.user) as |user|}}
                    {{userDisplayName user}}
                  {{/let}}</td>
              </tr>
            </:row>
            <:empty>
              <div class="alert alert-info">No quota ledgers</div>
            </:empty>
          </ResultsTable>

          {{#if @quotaLedgers.length}}
            {{#let
              (netQuotaForSession @quotaLedgers @sessionType)
              as |netQuota|
            }}
              <p>
                Net quota movement for this session:
                <span
                  class="font-monospace {{colorClassForAmount netQuota}}"
                >{{formatQuotaLedgerAmount netQuota @sessionType}}</span>
              </p>
            {{/let}}
          {{/if}}
        </:success>
      </Await>
    </div>
  </template>
}
